/*
 * Copyright (C) 2020-present Borza LLC. All rights reserved.
 */

import Head from "next/head";
import { Component } from "react";

import Banner from "../components/banner";
import Newsletter from "../components/newsletter";
import OpenGraphHead from "../components/og-head";
import TwitterHead from "../components/twitter-head";
import { toAbsolutePageURL } from "../utils/url";

interface IProps {
  query: {
    market: string;
  };
}

export default class Privacy extends Component<IProps> {
  public static getInitialProps({ query }: IProps) {
    query.market = query.market ?? "en-US";

    return { query };
  }

  public render() {
    const {
      query: {
        market,
      },
    } = this.props;

    const canonicalUrl = toAbsolutePageURL("/privacy", market);
    const alt = "Lasting Smiles";
    const title = "Privacy Policy · Lasting Smiles";
    const description = "Privacy policy for the best all natural, organic, fair trade, sustainable, cruelty free, dermatologist recommended lip balm, chapstick, lip care, and lip treatments for healing, hydrating, and moisturizing your dry, chapped lips.";

    return (
      <>
        <Head>
          <title>{title}</title>
          <meta
            content={description}
            name="description"
          />
          <link
            href={canonicalUrl}
            rel="canonical"
          />
          <link
            href={toAbsolutePageURL("/privacy", "en-US")}
            hrefLang="x-default"
            rel="alternate"
          />
          <link
            href={toAbsolutePageURL("/privacy", "en-US")}
            hrefLang="en-US"
            rel="alternate"
          />
          <link
            href={toAbsolutePageURL("/privacy", "en-CA")}
            hrefLang="en-CA"
            rel="alternate"
          />
          <script
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/", market),
                    "name": "Lasting Smiles",
                    "position": 1,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/privacy", market),
                    "name": "Privacy Policy",
                    "position": 2,
                  },
                ],
              }),
            }}
            type="application/ld+json"
          />
        </Head>
        <OpenGraphHead
          alt={alt}
          description={description}
          slug="privacy"
          title={title}
          type="website"
          url={canonicalUrl}
        />
        <TwitterHead
          alt={alt}
          description={description}
          slug="privacy"
          title={title}
        />
        <Banner />
        <Newsletter />
        <section className="px-2 py-10 sm:px-5">
          <div className="max-w-screen-lg mx-auto">
            <h1 className="font-medium font-serif text-3xl">Privacy Policy</h1>
            <p className="my-5">
              Last Updated: 3/7/2020
            </p>
            <p className="my-5">
              This Privacy Policy explains how information about you is collected, used, and disclosed by StartMark, SPC, a Washington social purpose corporation ("StartMark," "We," "Us," or "Our").
              This Privacy Policy applies to information We collect when you use Our website and other online products and services that link to this Privacy Policy (collectively, the "Services") or when you otherwise interact with Us.
            </p>

            <h2 className="font-medium font-serif text-2xl">Modifications</h2>
            <p className="my-5">
              We may change this Privacy Policy from time to time.
              If We make changes, We will notify you by revising the date at the top of the policy and, in some cases, We may provide you with additional notice (such as adding a statement to Our homepage or sending you a notification).
              We encourage you to review the Privacy Policy whenever you access the Services to stay informed about Our information practices and the ways you can help protect your privacy.
            </p>

            <h2 className="font-medium font-serif text-2xl">Collection of Information</h2>
            <h3 className="font-medium font-serif text-xl">Information You Provide Us</h3>
            <p className="my-5">
              We collect the information you provide directly to Us.
              For example, We collect information when you create an account, make a purchase, communicate with Us via third party social media sites, request customer support, post comments, participate in any interactive features of the Services, submit content, create your member profile, participate in a contest or promotion, or otherwise communicate with Us.
              The types of information We may collect include your name, email address, postal address, phone number, and any other information you choose to provide.
            </p>
            <h3 className="font-medium font-serif text-xl">Information We Collect Automatically When You Use the Services</h3>
            <p className="my-5">
              When you access or use Our Services, We automatically collect information about you, including:
            </p>
            <ul className="list-disc my-5">
              <li>
                Log Information.
                We log information about your use of the Services, including the type of browser you use, access times, pages viewed, your IP address, and the page you visited before navigating to Our Services.
              </li>
            </ul>
            <h3 className="font-medium font-serif text-xl">Information We Collect from Other Sources</h3>
            <p className="my-5">
              We may also obtain information from other sources and combine that with information We collect through Our Services.
              For example, if you create or log into your account through a social media site, We will have access to certain information from that site, such as your name, account information, and friends lists, in accordance with the authorization procedures determined by such social media site.
            </p>

            <h2 className="font-medium font-serif text-2xl">Use of Information</h2>
            <p className="my-5">
              We may use information about you for various purposes, including to:
            </p>
            <ul className="list-disc my-5">
              <li>Provide, maintain and improve Our Services;</li>
              <li>Provide and deliver the products and services you request, process transactions and send you related information, including confirmations and invoices;</li>
              <li>Send you technical notices, updates, security alerts, and support and administrative messages;</li>
              <li>Respond to your comments, questions, and requests and provide customer service;</li>
              <li>Communicate with you about products, services, offers, promotions, rewards, and events offered by StartMark and others, and provide news and information We think will be of interest to you;</li>
              <li>Monitor and analyze trends, usage and activities in connection with Our Services;</li>
              <li>Personalize and improve the Services and provide advertisements, content or features that match user profiles or interests;</li>
              <li>Process and deliver contest entries and rewards;</li>
              <li>Link or combine with information We get from others to help understand your needs and provide you with better service; and</li>
              <li>Carry out any other purpose for which the information was collected.</li>
            </ul>
            <p className="my-5">
              StartMark is based in the United States, and the information We collect is governed by U.S. law.
              By accessing or using the Services or otherwise providing information to Us, you consent to the processing and transfer of information in and to the U.S. and other countries.
            </p>

            <h2 className="font-medium font-serif text-2xl">Sharing of Information</h2>
            <p className="my-5">
              We may share information about you as follows or as otherwise described in this Privacy Policy:
            </p>
            <ul className="list-disc my-5">
              <li>With vendors, consultants and other service providers who need access to such information to carry out work on Our behalf;</li>
              <li>In response to a request for information if We believe disclosure is in accordance with any applicable law, regulation or legal process, or as otherwise required by any applicable law, rule or regulation;</li>
              <li>If We believe your actions are inconsistent with Our user agreements or policies, or to protect the rights, property, and safety of StartMark or others;</li>
              <li>In connection with, or during negotiations of, any merger, sale of company assets, financing or acquisition of all or a portion of Our business to another company; and</li>
              <li>With your consent or at your direction, including if We notify you through Our Services that the information you provide will be shared in a particular manner and you provide such information.</li>
            </ul>
            <p className="my-5">
              We may also share aggregated or de-identified information, which cannot reasonably be used to identify you.
            </p>

            <h2 className="font-medium font-serif text-2xl">Social Sharing Features</h2>
            <p className="my-5">
              The Services may offer social sharing features and other integrated tools (such as the Facebook "Like" button), which let you share actions you take on Our Services with other media, and vice versa.
              The use of such features enables the sharing of information with your friends or the public, depending on the settings you establish with the entity that provides the social sharing feature.
              For more information about the purpose and scope of data collection and processing in connection with social sharing features, please visit the privacy policies of the entities that provide these features.
            </p>

            <h2 className="font-medium font-serif text-2xl">Advertising and Analytics Services Provided by Others</h2>
            <p className="my-5">
              We may allow others to serve advertisements on Our behalf across the Internet and to provide analytics services.
              These entities may use cookies, web beacons and other technologies to collect information about your use of the Services and other websites, including your IP address, web browser, pages viewed, time spent on pages, links clicked and conversion information.
              StartMark and others may use this information to, among other things, analyze and track data, determine the popularity of certain content, deliver advertising and content targeted to your interests on Our Services and other websites and better understand your online activity.
              For more information about Internet-based ads, or to opt out of having your web browsing information used for behavioral advertising purposes, please visit <a className="underline" href="https://optout.networkadvertising.org/">optout.networkadvertising.org</a> and <a className="underline" href="https://optout.aboutads.info/">optout.aboutads.info</a>.
            </p>

            <h2 className="font-medium font-serif text-2xl">Security</h2>
            <p className="my-5">
              StartMark takes reasonable measures to help protect information about you from loss, theft, misuse, and unauthorized access, disclosure, alteration, and destruction.
            </p>

            <h2 className="font-medium font-serif text-2xl">Your Choices</h2>
            <h3 className="font-medium font-serif text-xl">Account Information</h3>
            <p className="my-5">
              You may update, correct, or delete information about you at any time by logging into your member account or emailing Us with your request at <a className="underline" href="mailto:info@lastingsmiles.org">info@lastingsmiles.org</a>.
              If you wish to deactivate your account, you may do so at any time by using the tools in your member account or by contacting Us at <a className="underline" href="mailto:info@lastingsmiles.org">info@lastingsmiles.org</a>, but note that We may retain certain information as required by law or for legitimate business purposes.
              We may also retain cached or archived copies of information about you for a certain period of time.
            </p>
            <h3 className="font-medium font-serif text-xl">Cookies</h3>
            <p className="my-5">
              Most web browsers are set to accept cookies by default.
              If you prefer, you can usually choose to set your browser to remove or reject browser cookies.
              Removing or rejecting browser cookies does not necessarily affect third-party cookies used in connection with Our Services.
            </p>
            <h3 className="font-medium font-serif text-xl">Promotional Communications</h3>
            <p className="my-5">
              You may opt-out of receiving promotional emails or messages from StartMark by following the instructions in those emails or messages or by using the tools in your member account.
              If you opt-out, We may still send you non-promotional communications, such as those about your account or Our ongoing service relationship.
            </p>

            <h2 className="font-medium font-serif text-2xl">Contact Us</h2>
            <p className="my-5">
              If you have any questions about this Privacy Policy, please contact Us at <a className="underline" href="mailto:info@lastingsmiles.org">info@lastingsmiles.org</a>.
            </p>
          </div>
        </section>
      </>
    );
  }
}
